import { useCallback, useEffect, useMemo } from 'react';
import {
  useDataColumns,
  useDataFetcher,
  usePagedDataFetcher,
} from '@/shared/hooks';
import type { DataResponse } from '../../model/types';
import { useDataFetchersContext } from '../DataFetchersContext';

interface DataTableData {
  loading: boolean;
  rows:
    | {
        [x: string]: string | number | boolean;
        id: number;
        isTotal: boolean;
      }[]
    | undefined;
  columns: string[];
  getNextPage: () => void;
}

export const useData = (): DataTableData => {
  const { onGetRowsPage, onGetTotalData } = useDataFetchersContext();
  const dataColumnsComparator = useCallback((a: string, b: string) => {
    return a.localeCompare(b, undefined, { numeric: true });
  }, []);
  const { dataColumns, addColumns, reset } = useDataColumns(
    dataColumnsComparator,
  );
  const { data: total, loading: totalLoading } = useDataFetcher(onGetTotalData);
  const {
    data: rows,
    loading,
    getNextPage,
  } = usePagedDataFetcher({
    getPage: onGetRowsPage,
    responseProcessor: (response) => {
      const { rows, columns, hasNextPage } = response as DataResponse;

      addColumns(columns);

      return { data: rows, hasNextPage };
    },
  });
  const preparedRows = useMemo(() => {
    if (!total && !rows) {
      return undefined;
    }

    return [
      ...(total
        ? [
            {
              id: -1,
              isTotal: true,
              ...total,
            },
          ]
        : []),
      ...(rows || []).map((row, i) => {
        return {
          id: i,
          isTotal: false,
          ...row,
        };
      }),
    ];
  }, [rows, total]);

  useEffect(() => {
    reset();
  }, [onGetRowsPage, reset]);
  useEffect(() => {
    addColumns(Object.keys(total ?? {}));
  }, [total, addColumns]);

  return {
    loading: totalLoading || loading,
    rows: preparedRows,
    columns: dataColumns,
    getNextPage,
  };
};
